import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../lib/api';

interface User {
    id: string;
    email: string;
    name?: string;
    avatar_url?: string;
}

interface AuthContextType {
    user: User | null;
    freeQuestionsCount: number;
    login: (credential: string) => Promise<void>;
    logout: () => void;
    incrementQuestions: () => void;
    isLimitReached: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const FREE_LIMIT = 3;

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [count, setCount] = useState(0);

    useEffect(() => {
        const savedUser = localStorage.getItem('diymech_user');
        const savedCount = localStorage.getItem('diymech_free_questions');
        if (savedUser) setUser(JSON.parse(savedUser));
        if (savedCount) setCount(parseInt(savedCount));
    }, []);

    const login = async (credential: string) => {
        try {
            if (credential === 'test-token-bypass') {
                const newUser = {
                    id: 'test-123',
                    email: 'test@diymech.com',
                    name: 'Test User',
                };
                setUser(newUser);
                localStorage.setItem('diymech_user', JSON.stringify(newUser));
                return;
            }

            const data = await api.loginWithGoogle(credential);
            if (data.success && data.user) {
                const newUser = {
                    id: data.user.id,
                    email: data.user.email,
                    name: data.user.name,
                    avatar_url: data.user.avatar_url,
                };
                setUser(newUser);
                localStorage.setItem('diymech_user', JSON.stringify(newUser));
            }
        } catch (error) {
            console.error('Login failed:', error);
            alert('Login failed. Please try again.');
        }
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('diymech_user');
    };

    const incrementQuestions = () => {
        if (!user) {
            const newCount = count + 1;
            setCount(newCount);
            localStorage.setItem('diymech_free_questions', newCount.toString());
        }
    };

    const isLimitReached = !user && count >= FREE_LIMIT;

    return (
        <AuthContext.Provider value={{
            user,
            freeQuestionsCount: count,
            login,
            logout,
            incrementQuestions,
            isLimitReached
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
}
